import {
  AnyAction,
  createListenerMiddleware,
  isAnyOf,
  isFulfilled,
} from '@reduxjs/toolkit';

import { login, logout, smartLogin } from '../actions/userActions';
import { SocialLoginType, TypeOfUser } from '../helpers/Constants';
import {
  AnalyticsLoginMethod,
  GA_EVENTS,
  IAnalyticsDefinition,
} from '../models/AnalyticsModel';
import { getAnalyticsUserRole } from './analyticsUserRole';
import { findFeatureEvent } from './featureEventMap';

interface ILoginPayload {
  approved: boolean;
  userId: string | null;
  userType: TypeOfUser;
}

const SOCIAL_LOGIN_METHODS: Record<SocialLoginType, AnalyticsLoginMethod> = {
  [SocialLoginType.google]: AnalyticsLoginMethod.google,
  [SocialLoginType.facebook]: AnalyticsLoginMethod.facebook,
  [SocialLoginType.apple]: AnalyticsLoginMethod.apple,
};

const getLoginMethod = (action: AnyAction): AnalyticsLoginMethod => {
  if (smartLogin.fulfilled.match(action)) {
    return AnalyticsLoginMethod.biometric;
  }

  const loginType: SocialLoginType | null = action.meta?.arg?.loginType ?? null;
  if (loginType === null) {
    return AnalyticsLoginMethod.email;
  }

  return SOCIAL_LOGIN_METHODS[loginType];
};

const isLoginFulfilled = isAnyOf(login.fulfilled, smartLogin.fulfilled);

const trackLogin = (adapter: IAnalyticsDefinition, action: AnyAction) => {
  const { approved, userId, userType }: ILoginPayload = action.payload ?? {};
  // AN UNAPPROVED ACCOUNT GETS A RESPONSE BUT NO SESSION, SO IT IS NOT A LOGIN
  const isSessionStarted = approved && !!userId;
  if (!isSessionStarted) {
    return;
  }

  adapter.setUser(String(userId));
  adapter.setUserProperties({ user_role: getAnalyticsUserRole(userType) });
  adapter.logCustomEvent({
    event: GA_EVENTS.login,
    method: getLoginMethod(action),
  });
};

const trackLogout = (adapter: IAnalyticsDefinition) => {
  adapter.setUser(null);
  adapter.setUserProperties({ user_role: null });
};

// ONE STORE-LEVEL LISTENER FOR EVERY EVENT THAT FOLLOWS A SUCCESSFUL THUNK.
// THUNKS REJECT THROUGH statusDispatch WHEN THE SERVER FAILS, SO A FULFILLED
// ACTION ALWAYS MEANS THE CHANGE WAS ACCEPTED
export const createAnalyticsListener = (adapter: IAnalyticsDefinition) => {
  const listener = createListenerMiddleware();

  listener.startListening({
    matcher: isFulfilled,
    effect: (action) => {
      if (isLoginFulfilled(action)) {
        trackLogin(adapter, action);
        return;
      }

      if (logout.fulfilled.match(action)) {
        trackLogout(adapter);
        return;
      }

      const event = findFeatureEvent(action);
      if (event !== null) {
        adapter.logCustomEvent(event);
      }
    },
  });

  return listener;
};
